import { Fragment, useCallback, useMemo } from 'react';
import SelectFilter from '@inovua/reactdatagrid-community/SelectFilter';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AddLink as AddLinkIcon } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';

import { DrugsApi, type ListDrugsParams } from '@/api/domains/drugs.api';
import { DrugsQueryKeys } from '@/api/domains/drugs.query-keys';
import type { Drug } from '@/api/domains/drugs.types';
import { DuplicateActionIconButton } from '@/components/actions';
import { ActionIconButton } from '@/components/buttons';
import { CustomTypography } from '@/components/CustomTypography';
import { useGlobalErrorStore } from '@/components/dialogs';
import { useNomenclatureStoreActions } from '@/components/nomenclature';
import { DataGrid, DataGridActionbar, type DataGridSelectionParams } from '@/components/ReactDataGrid';
import { formatDrugFullName, highlightKeyword } from '@/shared/helpers/format.helpers';
import { useInfiniteLoader } from '@/shared/lib/react-query';

import {
  useIsFilterPanelVisible,
  useNomenclatureSearchQuery,
  useReconciliationBindingActions,
  useSelectedUnboundDrug,
} from '../reconciliation-binding.store';

const SEARCH_DRUGS_LIMIT = 200;

const SearchQueryAwareText = ({ text }: { text: string }) => {
  const searchQuery = useNomenclatureSearchQuery();
  return searchQuery ? highlightKeyword(text, searchQuery) : text;
};

const defaultFilterValue = [
  { name: 'drug', type: 'select-by-full_name', operator: 'inlist', value: null },
  { name: 'maker', type: 'select-by-name', operator: 'inlist', value: null },
];

export const ReconciliationNomenclatureDataGrid = () => {
  const { t } = useTranslation('orders-page');
  const searchQuery = useNomenclatureSearchQuery();
  const isFilterPanelVisible = useIsFilterPanelVisible();
  const selectedUnboundDrug = useSelectedUnboundDrug();
  const { setSelectedNomenclatureDrug, openBindDialog } = useReconciliationBindingActions();
  const { setSelectedRow: setNomenclatureSelectedRow, openDuplicateModal } = useNomenclatureStoreActions();
  const { setGlobalError } = useGlobalErrorStore();

  const { mutate: openDuplicateWithFullDrug, isLoading: isFetchingDrugForDuplicate } = useMutation({
    mutationFn: (drugId: number) => DrugsApi.fetchDrug(drugId),
    onSuccess: fullDrug => {
      setNomenclatureSelectedRow(fullDrug);
      openDuplicateModal();
    },
    onError: setGlobalError,
  });

  const isEnabled = searchQuery.length >= 3;

  const params: ListDrugsParams = {
    query: searchQuery,
    limit: SEARCH_DRUGS_LIMIT,
  };

  const { isFetching, data, hasNextPage, fetchNextPage } = useInfiniteLoader({
    queryKey: DrugsQueryKeys.listDrugs(params),
    queryFn: ({ offset }) => DrugsApi.listDrugs({ offset, ...params }),
    offsetPerPage: SEARCH_DRUGS_LIMIT,
    enabled: isEnabled,
    keepPreviousData: isEnabled,
    staleTime: Infinity,
  });

  const pageData = data?.pages;
  const loadedPagesLength = pageData?.length ?? 0;

  const drugs = useMemo(() => {
    if (!pageData) return [];

    return pageData.flatMap(({ items }) => items.map(drug => ({ id: drug.id, drug, maker: drug.maker })));
  }, [pageData]);

  const drugFilterData = useMemo(
    () =>
      Array.from(new Map(drugs.map(d => [d.drug.full_name, true])).keys()).map(name => ({
        id: name,
        label: name,
      })),
    [drugs],
  );

  const makerFilterData = useMemo(
    () =>
      Array.from(
        new Map(
          drugs
            .filter((d): d is typeof d & { maker: NonNullable<typeof d.maker> } => Boolean(d.maker))
            .map(d => [d.maker.name, true]),
        ).keys(),
      ).map(name => ({
        id: name,
        label: name,
      })),
    [drugs],
  );

  const handleSelectionChange = useCallback(
    ({ data }: DataGridSelectionParams<{ id: number; drug: Drug; maker: Drug['maker'] }>) => {
      if (data) {
        const drug = (data as { drug: Drug }).drug;
        setSelectedNomenclatureDrug(drug);
        setNomenclatureSelectedRow(drug);
      }
    },
    [setSelectedNomenclatureDrug, setNomenclatureSelectedRow],
  );

  return (
    <DataGrid
      idProperty="id"
      dataSource={drugs}
      loading={isFetching}
      enableFiltering={isFilterPanelVisible}
      defaultFilterValue={defaultFilterValue}
      paginationProps={{
        hasNextPage,
        itemsPerPage: SEARCH_DRUGS_LIMIT,
        pagesLength: loadedPagesLength,
        onShowMore: fetchNextPage,
      }}
      onSelectionChange={handleSelectionChange}
      onRowDoubleClick={openBindDialog}
      shareSpaceOnResize
      columns={[
        {
          name: 'drug',
          header: t('reconciliation.binding.columns.product-name'),
          minWidth: 250,
          defaultFlex: 1,
          filterEditor: SelectFilter,
          filterEditorProps: {
            multiple: true,
            wrapMultiple: false,
            placeholder: t('reconciliation.binding.columns.product-name'),
            dataSource: drugFilterData,
          },
          render: ({
            value: drug,
            rowIndex,
            data,
            setRowSelected,
          }: {
            value: Drug;
            rowIndex: number;
            data: any;
            setRowSelected: (index: number, data: any) => void;
          }) => {
            const drugName = formatDrugFullName(drug) ?? '';

            const handleRowSelect = (event: React.MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation();
              setRowSelected(rowIndex, data);
            };

            const handleBindClick = (event: React.MouseEvent<HTMLButtonElement>) => {
              handleRowSelect(event);
              openBindDialog();
            };

            const handleDuplicateClick = (event: React.MouseEvent<HTMLButtonElement>) => {
              handleRowSelect(event);
              openDuplicateWithFullDrug(drug.id);
            };

            return (
              <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                <CustomTypography noWrap lineClamp={2} component="p" variant="body2" fontWeight={500} title={drugName}>
                  <SearchQueryAwareText text={drugName} />
                </CustomTypography>
                <DataGridActionbar>
                  <ActionIconButton
                    disabled={!selectedUnboundDrug}
                    title={t('reconciliation.binding.actions.bind')}
                    onClick={handleBindClick}
                  >
                    <AddLinkIcon fontSize="medium" />
                  </ActionIconButton>
                  <DuplicateActionIconButton disabled={isFetchingDrugForDuplicate} onClick={handleDuplicateClick} />
                </DataGridActionbar>
              </Stack>
            );
          },
        },
        {
          name: 'maker',
          minWidth: 180,
          defaultFlex: 1,
          header: t('reconciliation.binding.columns.manufacturer'),
          filterEditor: SelectFilter,
          filterEditorProps: {
            multiple: true,
            wrapMultiple: false,
            placeholder: t('reconciliation.binding.columns.manufacturer'),
            dataSource: makerFilterData,
          },
          render: ({ value: maker }: { value: Drug['maker'] }) => {
            if (!maker) return null;

            return (
              <Fragment>
                <Typography component="p" variant="body2">
                  <SearchQueryAwareText text={maker.name} />
                </Typography>
                {maker.country && (
                  <Typography component="p" variant="caption" color="text.secondary">
                    {maker.country}
                  </Typography>
                )}
              </Fragment>
            );
          },
        },
      ]}
    />
  );
};
